import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  FileVideo,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react';
import {
  api,
  type KillImportJob,
  type KillImportMatchedRow,
  type KillImportPreview,
  type KillImportUnmatchedRow,
} from '@/lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
  initialJobId?: string | null;
}

type Resolution =
  | { kind: 'matched'; ocrName: string; row: KillImportMatchedRow; included: boolean }
  | {
      kind: 'unmatched';
      ocrName: string;
      row: KillImportUnmatchedRow;
      playerId: number | null;
      included: boolean;
    };

function formatMillions(m: number): string {
  return m.toFixed(1);
}

function formatRaw(n: number): string {
  return n.toLocaleString('en-US');
}

export default function ImportKillsDialog({
  open,
  onClose,
  onImported,
  initialJobId = null,
}: Props) {
  const qc = useQueryClient();
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(initialJobId);
  const [preview, setPreview] = useState<KillImportPreview | null>(null);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);

  const jobQuery = useQuery({
    queryKey: ['kills-import-job', jobId],
    queryFn: () => api.getKillsImportJob(jobId!),
    enabled: !!jobId && !preview,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s && (s === 'done' || s === 'failed') ? false : 1500;
    },
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      if (!videoFile) throw new Error('No video');
      return api.uploadKillsImportVideo(videoFile);
    },
    onSuccess: (data) => setJobId(data.jobId),
  });

  const previewMutation = useMutation({
    mutationFn: (rows: Array<{ name: string; kills: number }>) =>
      api.previewKillsImport(rows),
    onSuccess: (data) => {
      setPreview(data);
      setResolutions([
        ...data.matched.map<Resolution>((row) => ({
          kind: 'matched',
          ocrName: row.ocrName,
          row,
          included: !row.regression && !row.unchanged,
        })),
        ...data.unmatched.map<Resolution>((row) => ({
          kind: 'unmatched',
          ocrName: row.ocrName,
          row,
          playerId: row.suggestions[0]?.playerId ?? null,
          included: false,
        })),
      ]);
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryKillsImportJob(jobId!),
    onSuccess: (data: KillImportJob) => {
      qc.setQueryData(['kills-import-job', jobId], data);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () =>
      api.confirmKillsImport(
        resolutions
          .filter((r) => r.included)
          .map((r) => ({
            playerId: r.kind === 'matched' ? r.row.player.id : r.playerId,
            kills: r.kind === 'matched' ? r.row.kills : r.row.kills,
          }))
          .filter((r): r is { playerId: number; kills: number } => r.playerId != null),
        jobId,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roster'] });
      qc.invalidateQueries({ queryKey: ['players'] });
      qc.invalidateQueries({ queryKey: ['kills-import-jobs'] });
      onImported();
      reset();
      onClose();
    },
  });

  useEffect(() => {
    const j = jobQuery.data;
    if (!j || preview) return;
    if (j.status === 'done' && j.extraction) {
      previewMutation.mutate(j.extraction.rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobQuery.data?.status]);

  function reset() {
    setVideoFile(null);
    setJobId(null);
    setPreview(null);
    setResolutions([]);
    uploadMutation.reset();
    previewMutation.reset();
    confirmMutation.reset();
    retryMutation.reset();
  }

  const included = useMemo(
    () =>
      resolutions
        .filter((r) => r.included)
        .map((r) => (r.kind === 'matched' ? r.row.player.id : r.playerId))
        .filter((id): id is number => id != null),
    [resolutions],
  );

  if (!open) return null;

  const stageText = (() => {
    const j = jobQuery.data;
    if (!j) return 'Uploading…';
    if (j.status === 'failed') return `Failed: ${j.error ?? 'unknown'}`;
    if (j.stage === 'uploading') return 'Uploading video…';
    if (j.stage === 'processing') return 'Extracting kill counts…';
    return 'Working…';
  })();

  const isFailed = jobQuery.data?.status === 'failed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-slate-800 bg-slate-900 p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Import kills from video</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {/* Step 1: pick file */}
        {!jobId && !preview && (
          <div className="mt-4 space-y-4">
            <div className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2 text-xs text-slate-400">
              Record the in-game Alliance → Members → Kills list. Pan slowly
              so every row is visible for at least a second.
            </div>

            <label
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f && f.type.startsWith('video/')) setVideoFile(f);
              }}
              className="flex cursor-pointer flex-col gap-2 rounded border border-dashed border-slate-700 bg-slate-950/50 p-4 text-sm text-slate-300 hover:border-slate-500"
            >
              <span className="inline-flex items-center gap-2 text-slate-200">
                <FileVideo size={16} />
                {videoFile ? videoFile.name : 'Drop or select .mp4 / .mov'}
              </span>
              <input
                type="file"
                accept="video/mp4,video/quicktime"
                className="hidden"
                onChange={(e) => setVideoFile(e.target.files?.[0] ?? null)}
              />
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={() => uploadMutation.mutate()}
                disabled={!videoFile || uploadMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
              >
                <Upload size={14} /> Process
              </button>
            </div>
            {uploadMutation.isError && (
              <div className="text-sm text-red-400">
                {(uploadMutation.error as Error)?.message ?? 'Upload failed'}
              </div>
            )}
          </div>
        )}

        {/* Step 2: job progress */}
        {jobId && !preview && (
          <div className="mt-6 space-y-3">
            {!isFailed && (
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <div className="h-2 flex-1 overflow-hidden rounded bg-slate-800">
                  <div
                    className="h-full bg-indigo-500 transition-all"
                    style={{
                      width:
                        jobQuery.data?.stage === 'processing'
                          ? '80%'
                          : jobQuery.data?.stage === 'uploading'
                            ? '40%'
                            : '10%',
                    }}
                  />
                </div>
                <span>{stageText}</span>
              </div>
            )}

            {isFailed && (
              <div className="space-y-2 rounded border border-red-800 bg-red-950/40 p-3 text-sm text-red-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5" />
                  <div>
                    <div className="font-medium">Gemini extraction failed</div>
                    <div className="mt-1 text-xs text-red-300/80">
                      {jobQuery.data?.error ?? 'unknown error'}
                    </div>
                    {typeof jobQuery.data?.attempts === 'number' && (
                      <div className="text-xs text-red-300/60">
                        Attempts: {jobQuery.data.attempts}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => retryMutation.mutate()}
                    disabled={retryMutation.isPending || !jobQuery.data?.videoAvailable}
                    className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
                  >
                    <RefreshCw size={14} /> Retry
                  </button>
                  <button
                    onClick={() => {
                      reset();
                    }}
                    className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
                  >
                    Upload a different video
                  </button>
                </div>
                {!jobQuery.data?.videoAvailable && (
                  <div className="text-xs text-red-300/70">
                    Stored video is no longer on disk — re-upload to retry.
                  </div>
                )}
                {retryMutation.isError && (
                  <div className="text-xs text-red-300">
                    {(retryMutation.error as Error)?.message ?? 'Retry failed'}
                  </div>
                )}
              </div>
            )}

            {!isFailed && (
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>Runs in the background — you can close this dialog and check back later.</span>
                <button
                  onClick={onClose}
                  className="rounded border border-slate-700 px-3 py-1 text-sm text-slate-300 hover:bg-slate-800"
                >
                  Check later
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: preview */}
        {preview && (
          <>
            <PreviewView
              preview={preview}
              resolutions={resolutions}
              setResolutions={setResolutions}
              includedCount={included.length}
              onCancel={() => reset()}
              onConfirm={() => confirmMutation.mutate()}
              confirming={confirmMutation.isPending}
            />
            {confirmMutation.isError && (
              <div className="mt-2 rounded border border-red-800 bg-red-950/40 p-2 text-sm text-red-200">
                {(confirmMutation.error as Error)?.message ?? 'Import failed'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PreviewView({
  preview,
  resolutions,
  setResolutions,
  includedCount,
  onCancel,
  onConfirm,
  confirming,
}: {
  preview: KillImportPreview;
  resolutions: Resolution[];
  setResolutions: (r: Resolution[]) => void;
  includedCount: number;
  onCancel: () => void;
  onConfirm: () => void;
  confirming: boolean;
}) {
  function toggle(ocrName: string) {
    setResolutions(
      resolutions.map((r) =>
        r.ocrName === ocrName ? { ...r, included: !r.included } : r,
      ),
    );
  }
  function setPlayer(ocrName: string, playerId: number | null) {
    setResolutions(
      resolutions.map((r) =>
        r.kind === 'unmatched' && r.ocrName === ocrName
          ? { ...r, playerId, included: playerId != null }
          : r,
      ),
    );
  }

  const regressions = resolutions.filter(
    (r) => r.kind === 'matched' && r.row.regression,
  ).length;
  const unchanged = resolutions.filter(
    (r) => r.kind === 'matched' && r.row.unchanged,
  ).length;

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-slate-300">
          <b className="text-white">{preview.matched.length}</b> matched ·{' '}
          {preview.unmatched.length} unmatched
          {regressions > 0 && (
            <> · <span className="text-amber-400">{regressions} regressions</span></>
          )}
          {unchanged > 0 && (
            <> · <span className="text-slate-500">{unchanged} unchanged</span></>
          )}
        </span>
        <span className="text-slate-500">{includedCount} rows will be imported</span>
      </div>

      <div className="max-h-96 overflow-y-auto rounded border border-slate-800">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-950/80 text-xs text-slate-500">
            <tr>
              <th className="w-8 px-2 py-2 text-left"></th>
              <th className="px-2 py-2 text-left">From video</th>
              <th className="px-2 py-2 text-left">Player</th>
              <th className="px-2 py-2 text-right">Kills (raw)</th>
              <th className="px-2 py-2 text-right">M</th>
              <th className="px-2 py-2 text-right">Baseline (M)</th>
            </tr>
          </thead>
          <tbody>
            {resolutions.map((r) => {
              const disabled =
                r.kind === 'unmatched'
                  ? r.playerId == null
                  : r.row.regression || r.row.unchanged;
              return (
                <tr
                  key={r.ocrName}
                  className={
                    'border-t border-slate-800/70 ' +
                    (r.kind === 'matched' && r.row.regression
                      ? 'bg-amber-950/20'
                      : '')
                  }
                >
                  <td className="px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={r.included}
                      onChange={() => toggle(r.ocrName)}
                      disabled={r.kind === 'unmatched' && r.playerId == null}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-slate-300">
                    {r.ocrName}
                    {r.kind === 'matched' && r.row.regression && (
                      <span className="ml-2 rounded bg-amber-950/60 px-1.5 py-0.5 text-[10px] uppercase text-amber-300">
                        regression
                      </span>
                    )}
                    {r.kind === 'matched' && r.row.unchanged && (
                      <span className="ml-2 rounded bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase text-slate-400">
                        unchanged
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {r.kind === 'matched' ? (
                      <span className="inline-flex items-center gap-1.5 text-slate-200">
                        <CheckCircle2 size={12} className="text-emerald-500" />
                        {r.row.player.displayName ?? r.row.player.name}
                      </span>
                    ) : (
                      <select
                        value={r.playerId ?? ''}
                        onChange={(e) =>
                          setPlayer(r.ocrName, e.target.value ? Number(e.target.value) : null)
                        }
                        className="rounded border border-slate-700 bg-slate-950 px-1.5 py-1 text-xs text-slate-100"
                      >
                        <option value="">— skip —</option>
                        {r.row.suggestions.map((s) => (
                          <option key={s.playerId} value={s.playerId}>
                            {s.displayName ?? s.name} ({Math.round(s.score * 100)}%)
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-200">
                    {formatRaw(r.kind === 'matched' ? r.row.kills : r.row.kills)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-300">
                    {formatMillions(r.kind === 'matched' ? r.row.killsM : r.row.killsM)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">
                    {r.kind === 'matched' ? formatMillions(r.row.baselineKillsM) : '—'}
                  </td>
                </tr>
              );
            })}
            {resolutions.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-6 text-center text-slate-500">
                  No rows extracted.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming || includedCount === 0}
          className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
        >
          Import {includedCount} rows
        </button>
      </div>
    </div>
  );
}
